import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { EvaluationCategory, EvaluationCategoryInput } from '@/types/employeeEvaluations';

export const useEvaluationCategories = (brandId: string | null) => {
  const [categories, setCategories] = useState<EvaluationCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchCategories = useCallback(async () => {
    if (!brandId) return;
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('os_employee_evaluation_categories')
        .select('*')
        .eq('brand_id', brandId)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setCategories((data || []) as EvaluationCategory[]);
    } catch (error: any) {
      toast.error('Failed to load evaluation categories');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const createCategory = async (input: EvaluationCategoryInput): Promise<EvaluationCategory | null> => {
    if (!brandId) return null;
    try {
      const { data, error } = await supabase
        .from('os_employee_evaluation_categories')
        .insert({ ...input, brand_id: brandId })
        .select()
        .single();

      if (error) throw error;
      toast.success('Category created');
      await fetchCategories();
      return data as EvaluationCategory;
    } catch (error: any) {
      toast.error(error.message || 'Failed to create category');
      return null;
    }
  };

  const updateCategory = async (id: string, input: Partial<EvaluationCategoryInput>): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('os_employee_evaluation_categories')
        .update({ ...input, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      toast.success('Category updated');
      await fetchCategories();
      return true;
    } catch (error: any) {
      toast.error(error.message || 'Failed to update category');
      return false;
    }
  };

  const deleteCategory = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('os_employee_evaluation_categories')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Category deleted');
      await fetchCategories();
      return true;
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete category');
      return false;
    }
  };

  const initializeDefaultCategories = async (): Promise<boolean> => {
    if (!brandId) return false;
    try {
      const defaultCategories = [
        { code: 'HS', name: 'Hard Skills', description: 'Technical and job-specific competencies', color: '#3b82f6' },
        { code: 'SS', name: 'Soft Skills', description: 'Interpersonal and behavioral competencies', color: '#8b5cf6' },
      ];

      for (const cat of defaultCategories) {
        const { error } = await supabase
          .from('os_employee_evaluation_categories')
          .upsert({ ...cat, brand_id: brandId }, { onConflict: 'brand_id,code' });
        if (error) console.error('Error upserting category:', error);
      }

      await fetchCategories();
      toast.success('Default categories initialized');
      return true;
    } catch (error: any) {
      toast.error('Failed to initialize categories');
      return false;
    }
  };

  return {
    categories,
    isLoading,
    refetch: fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    initializeDefaultCategories,
  };
};
